/**
 * ArchivedStoresPage — list archived CloudGM Business stores.
 * Variants: 'personal' | 'company' (determined by URL param via wrapper or prop).
 * Actions: Restore, Hard-delete (requires typing 'DELETE').
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, RotateCcw, Trash2, Search, Store, AlertTriangle,
  Mail, Phone, User, Building2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtDate(s, language) {
  if (!s) return language === 'ar' ? '—' : '—';
  try {
    return new Date(s).toLocaleString('en-GB', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}

export default function ArchivedStoresPage() {
  const navigate = useNavigate();
  const { variant: routeVariant } = useParams();
  const variant = routeVariant === 'company' ? 'company' : 'personal';
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmInput, setConfirmInput] = useState('');

  const titleAr = variant === 'company' ? 'أرشيف متاجر الشركات' : 'أرشيف المتاجر الشخصية';
  const titleEn = variant === 'company' ? 'Company Stores Archive' : 'Personal Stores Archive';
  const Icon = variant === 'company' ? Building2 : User;
  const iconColor = variant === 'company' ? '#14b8a6' : '#a855f7';

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/stores/archived?business_type=${variant}`,
        { headers: getAuthHeaders() },
      );
      setItems(res.data?.data || []);
    } catch (err) {
      toast.error(isAr ? 'فشل تحميل الأرشيف' : 'Failed to load archive');
    } finally {
      setLoading(false);
    }
  }, [variant, isAr]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const restore = async (store) => {
    setProcessingId(store.id);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/stores/${store.id}/restore`,
        {},
        { headers: getAuthHeaders() },
      );
      toast.success(isAr ? 'تم استعادة المتجر' : 'Store restored');
      fetchItems();
    } catch (err) {
      const msg = err.response?.data?.detail || (isAr ? 'فشلت الاستعادة' : 'Restore failed');
      toast.error(typeof msg === 'string' ? msg : (isAr ? 'فشلت الاستعادة' : 'Restore failed'));
    } finally {
      setProcessingId(null);
    }
  };

  const hardDelete = async () => {
    if (!confirmDelete) return;
    if (confirmInput.trim().toUpperCase() !== 'DELETE') {
      toast.error(isAr ? 'الرجاء كتابة DELETE للتأكيد' : 'Please type DELETE to confirm');
      return;
    }
    setProcessingId(confirmDelete.id);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/stores/${confirmDelete.id}/hard`,
        { headers: getAuthHeaders() },
      );
      toast.success(isAr ? 'تم حذف المتجر نهائياً' : 'Store permanently deleted');
      setConfirmDelete(null);
      setConfirmInput('');
      fetchItems();
    } catch (err) {
      const msg = err.response?.data?.detail || (isAr ? 'فشل الحذف' : 'Delete failed');
      toast.error(typeof msg === 'string' ? msg : (isAr ? 'فشل الحذف' : 'Delete failed'));
    } finally {
      setProcessingId(null);
    }
  };

  const filtered = items.filter((s) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [s.name, s.nameEn, s.owner_name, s.owner_email, s.owner_phone, s.id]
      .filter(Boolean)
      .some((t) => String(t).toLowerCase().includes(q));
  });

  return (
    <DashboardLayout
      title={titleAr}
      titleEn={titleEn}
      subtitle={isAr ? 'استعادة أو حذف نهائي للمتاجر المؤرشفة' : 'Restore or permanently delete archived stores'}
      screenCode={variant === 'company' ? 'ARC-CGB-COM' : 'ARC-CGB-PER'}
      backPath="/control/archive/cloudgm-business"
      onBack={() => navigate('/control/archive/cloudgm-business')}
      breadcrumb={[
        { labelAr: 'الأرشيف', labelEn: 'Archive', path: '/control/archive' },
        { labelAr: 'CLOUDGM Business', labelEn: 'CLOUDGM Business', path: '/control/archive/cloudgm-business' },
        { labelAr: titleAr, labelEn: titleEn },
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid={`archived-stores-${variant}`}>
        {/* Search */}
        <div className="relative max-w-xl mb-6">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isAr ? 'بحث باسم المتجر أو المالك أو البريد...' : 'Search by store name, owner, or email...'}
            className="w-full ps-3 pe-9 py-3 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl text-white text-sm focus:border-[#d4af37]/50 focus:outline-none"
            data-testid="archived-stores-search"
          />
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-[#0a0a0a] rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-[#808080] bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
            <Store className="h-16 w-16 mx-auto mb-3 opacity-30" />
            <p className="text-lg">{isAr ? 'لا توجد متاجر مؤرشفة' : 'No archived stores'}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((store) => {
              const stoppedReason = store.stopped_reason || store.reject_reason;
              return (
                <div
                  key={store.id}
                  className="p-5 bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] hover:border-[#d4af37]/40 transition-all"
                  data-testid={`archived-store-${store.id}`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-3">
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center border"
                          style={{ backgroundColor: `${iconColor}20`, borderColor: `${iconColor}40` }}
                        >
                          <Icon className="h-6 w-6" style={{ color: iconColor }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-white text-lg truncate">
                            {store.name || store.nameEn || (isAr ? 'بدون اسم' : 'No name')}
                          </h3>
                          {store.nameEn && store.name && (
                            <p className="text-sm text-[#808080] truncate">{store.nameEn}</p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                        <div className="bg-[#0a0a0a] rounded-xl p-3">
                          <p className="text-xs text-[#606060] mb-1">
                            {isAr ? 'المالك' : 'Owner'}
                          </p>
                          <p className="text-sm text-white font-medium truncate">
                            {store.owner_name || '—'}
                          </p>
                        </div>
                        <div className="bg-[#0a0a0a] rounded-xl p-3">
                          <p className="text-xs text-[#606060] mb-1">
                            {isAr ? 'تاريخ الأرشفة' : 'Archived at'}
                          </p>
                          <p className="text-sm text-white font-medium">{fmtDate(store.archived_at, language)}</p>
                        </div>
                        <div className="bg-[#0a0a0a] rounded-xl p-3">
                          <p className="text-xs text-[#606060] mb-1">
                            {isAr ? 'الحالة الأخيرة' : 'Last status'}
                          </p>
                          <p className="text-sm text-white font-medium">{store.status || '—'}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-4 text-sm mb-2">
                        {store.owner_email && (
                          <span className="flex items-center gap-1 text-[#808080]">
                            <Mail className="h-4 w-4" />
                            {store.owner_email}
                          </span>
                        )}
                        {store.owner_phone && (
                          <span className="flex items-center gap-1 text-[#808080]">
                            <Phone className="h-4 w-4" />
                            <span dir="ltr">{store.owner_phone}</span>
                          </span>
                        )}
                      </div>

                      {stoppedReason && (
                        <div className="mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                          <p className="text-xs text-red-400 mb-1">
                            {isAr ? 'سبب الإيقاف/الرفض:' : 'Stop/Reject reason:'}
                          </p>
                          <p className="text-sm text-white">{stoppedReason}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 min-w-[140px]">
                      <Button
                        onClick={() => restore(store)}
                        disabled={processingId === store.id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`restore-store-${store.id}`}
                      >
                        {processingId === store.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <RotateCcw className="h-4 w-4 me-2" />
                            {isAr ? 'استعادة' : 'Restore'}
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => {
                          setConfirmDelete(store);
                          setConfirmInput('');
                        }}
                        disabled={processingId === store.id}
                        variant="outline"
                        className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                        data-testid={`hard-delete-store-${store.id}`}
                      >
                        <Trash2 className="h-4 w-4 me-2" />
                        {isAr ? 'حذف نهائي' : 'Hard Delete'}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Hard-delete confirmation */}
      <Dialog
        open={!!confirmDelete}
        onOpenChange={(v) => {
          if (!v) {
            setConfirmDelete(null);
            setConfirmInput('');
          }
        }}
      >
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white max-w-lg" data-testid="hard-delete-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              {isAr ? 'حذف نهائي' : 'Permanent Delete'}
            </DialogTitle>
            <DialogDescription className="text-[#808080]">
              {isAr
                ? `سيُحذف المتجر "${confirmDelete?.name || ''}" نهائياً ولن يمكن استعادته.`
                : `Store "${confirmDelete?.name || ''}" will be permanently deleted and cannot be recovered.`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <label className="text-sm text-[#808080] mb-2 block">
              {isAr ? 'اكتب DELETE للتأكيد:' : 'Type DELETE to confirm:'}
            </label>
            <Input
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder="DELETE"
              className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              data-testid="hard-delete-confirm-input"
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDelete(null);
                setConfirmInput('');
              }}
              className="border-[#2a2a4e] text-white"
            >
              {isAr ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={hardDelete}
              disabled={confirmInput.trim().toUpperCase() !== 'DELETE' || !!processingId}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="hard-delete-confirm-btn"
            >
              {processingId === confirmDelete?.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Trash2 className="h-4 w-4 me-2" />
                  {isAr ? 'حذف نهائي' : 'Delete permanently'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
